import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { crmApi } from '@/api/crm';
import { toast } from '@/stores/toastStore';
import { Modal, ModalHeader } from './_ui';

/**
 * SIMG-COMMERCIAL-LEADBETON-2026-001 — Modale création rapide client/prospect.
 * Utilisée depuis NouvelleProformaPage (sélecteur) et ClientsPage (bouton
 * répertoire). Auto-sélection via `onCreated`.
 */
export function NouveauClientDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [fullName, setFN]       = useState('');
  const [companyName, setCN]    = useState('');
  const [contactType, setCT]    = useState<'client' | 'prospect'>('prospect');
  const [phone, setPhone]       = useState('');
  const [email, setEmail]       = useState('');

  const creerMut = useMutation({
    mutationFn: (body: Parameters<typeof crmApi.createContact>[0]) => crmApi.createContact(body),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['crm', 'contacts', 'commercial'] });
      qc.invalidateQueries({ queryKey: ['crm', 'contacts'] });
      toast.success('Contact créé', c.fullName);
      onCreated(c.id);
    },
  });

  const canSubmit = fullName.trim().length >= 2;
  const submit = () => {
    if (!canSubmit) return;
    creerMut.mutate({
      contactType,
      fullName:    fullName.trim(),
      companyName: companyName.trim() || undefined,
      phone:       phone.trim() || undefined,
      email:       email.trim() || undefined,
    });
  };

  return (
    <Modal onClose={onClose}>
      <div style={{ maxWidth: 480, width: '100%', padding: 20 }}>
        <ModalHeader titre="Nouveau client / prospect" onClose={onClose} />
        <p style={{ fontSize: 13, color: 'var(--tx-3, #6B7280)', marginTop: 0 }}>
          Champs minimum. Complète RCCM/NCC/adresse plus tard depuis <a href="/crm" target="_blank" rel="noreferrer">/crm</a>.
        </p>

        <div style={{ display: 'grid', gap: 10 }}>
          <label>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>Nom complet *</div>
            <input
              autoFocus type="text" value={fullName} onChange={(e) => setFN(e.target.value)}
              placeholder="ex. Kouassi Yao"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2, #D1D5DB)', borderRadius: 8, fontSize: 14 }}
            />
          </label>
          <label>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>Raison sociale (B2B)</div>
            <input
              type="text" value={companyName} onChange={(e) => setCN(e.target.value)}
              placeholder="ex. BTP Bouaké SA (laisser vide pour un particulier)"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8, fontSize: 14 }}
            />
          </label>
          <label>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>Type</div>
            <select
              value={contactType} onChange={(e) => setCT(e.target.value as 'client' | 'prospect')}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8, fontSize: 14, background: '#fff' }}
            >
              <option value="prospect">Prospect (pas encore engagé)</option>
              <option value="client">Client (déjà travaille avec nous)</option>
            </select>
          </label>
          <label>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>Téléphone</div>
            <input
              type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+225 07 07 07 07 07"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8, fontSize: 14 }}
            />
          </label>
          <label>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>Email</div>
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@client.ci"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8, fontSize: 14 }}
            />
          </label>
        </div>

        <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--bd-1)', background: '#fff', cursor: 'pointer' }}
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit || creerMut.isPending}
            style={{
              padding: '8px 14px', borderRadius: 8, border: 'none',
              background: canSubmit ? 'var(--pr-1, #1F4E79)' : '#D1D5DB', color: '#fff', fontWeight: 700,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {creerMut.isPending ? 'Création…' : 'Créer et sélectionner'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
